import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "c-feature-carousel__inner" }
const _hoisted_3 = { class: "swiper-wrapper" }
const _hoisted_4 = ["href", "target", "rel", "aria-label"]
const _hoisted_5 = { class: "c-feature-carousel__content" }

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import { destroySwiper } from '@/utils/site-utils';
import type { ImageField } from '@/types/sitecore';
import utils from '@/utils';
import 'swiper/css';

interface FeatureCarouselItemFields {
  image?: {
    fields?: {
      desktopImage?: ImageField;
      tabletImage?: ImageField;
      mobileImage?: ImageField;
    };
  };
  title?: { value?: string };
  description?: { value?: string; editable?: string };
  link?: { value?: { href?: string; text?: string; title?: string; target?: string } };
}

interface FeatureCarouselItem {
  id: string;
  fields?: FeatureCarouselItemFields | null;
}

interface NormalizedItem extends FeatureCarouselItem {
  fields: FeatureCarouselItemFields;
  imageField?: {
    fields: {
      desktopImage: ImageField;
      tabletImage: ImageField;
      mobileImage: ImageField;
    };
  };
  href: string;
  target?: string;
  linkLabel: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FeatureCarousel',
  props: {
  fields: {
    type: Object,
    default: null,
  },
  rendering: {
    type: Object,
    default: null,
  },
  params: {
    type: Object,
    default: null,
  },
  dataSource: {
    type: String,
    default: '',
  },
},
  setup(__props) {

const props = __props;

let swiper: Swiper | null = null;
const swiperEl = ref<HTMLElement | null>(null);
const paginationEl = ref<HTMLElement | null>(null);
const viewportWidth = ref(0);

const rawItems = computed<FeatureCarouselItem[]>(() => {
  const cards = props.fields?.items;
  return Array.isArray(cards) ? cards : [];
});

const getAdaptiveImageField = (image?: FeatureCarouselItemFields['image'], alt = '') => {
  if (image?.fields?.desktopImage?.value?.src) {
    const desktopImage = image.fields.desktopImage;
    const tabletImage = image.fields.tabletImage?.value?.src
      ? image.fields.tabletImage
      : desktopImage;
    const mobileImage = image.fields.mobileImage?.value?.src
      ? image.fields.mobileImage
      : desktopImage;

    return {
      fields: {
        desktopImage,
        tabletImage,
        mobileImage,
      },
    };
  }

  return undefined;
};

const items = computed<NormalizedItem[]>(() =>
  rawItems.value.reduce<NormalizedItem[]>((result, item, index) => {
    const itemFields = item.fields;
    if (!itemFields) return result;

    const title = itemFields.title?.value || `Feature ${index + 1}`;
    const linkValue = itemFields.link?.value || {};

    result.push({
      ...item,
      fields: itemFields,
      imageField: getAdaptiveImageField(itemFields.image, title),
      href: linkValue.href || '#',
      target: linkValue.target,
      linkLabel: linkValue.text || linkValue.title || title,
    });

    return result;
  }, [])
);

const rootClass = computed(() => [
  utils.overall.baseComClass(props?.fields, 'c-feature-carousel'),
  {
    'c-feature-carousel--compact': items.value.length < 5,
    'c-feature-carousel--three': items.value.length === 3,
    'c-feature-carousel--four': items.value.length === 4,
  },
]);
const rootStyle = computed(() => utils.overall?.baseComStyle?.(props.fields) || {});
const isDesktop = computed(() => viewportWidth.value >= 1200);
const isCompact = computed(() => items.value.length < 5);
const shouldShowPagination = computed(
  () => items.value.length > 1 && (!isDesktop.value || !isCompact.value)
);
const slidesPerViewDesktop = computed(() => (isCompact.value ? items.value.length : 5));

const updateViewportWidth = () => {
  viewportWidth.value = window.innerWidth;
};

const buildSwiper = () => {
  if (!swiperEl.value || !items.value.length) return;

  destroySwiper(swiper, true, true);
  swiper = new Swiper(swiperEl.value, {
    modules: [Pagination],
    // Keep a little more of the next card visible on mobile so the cards do not dominate the screen.
    slidesPerView: 1.8,
    spaceBetween: 18,
    centeredSlides: false,
    watchOverflow: true,
    resizeObserver: true,
    observer: true,
    observeParents: true,
    pagination:
      shouldShowPagination.value && paginationEl.value
        ? {
            el: paginationEl.value,
            clickable: true,
            bulletElement: 'span',
          }
        : false,
    breakpoints: {
      744: {
        slidesPerView: 'auto',
        spaceBetween: 24,
      },
      1200: {
        slidesPerView: slidesPerViewDesktop.value,
        spaceBetween: 28,
      },
      1440: {
        slidesPerView: slidesPerViewDesktop.value,
        spaceBetween: 32,
      },
    },
  });
};

watch(
  () => [items.value.length, shouldShowPagination.value, slidesPerViewDesktop.value],
  () => nextTick(() => buildSwiper())
);

onMounted(() => {
  updateViewportWidth();
  window.addEventListener('resize', updateViewportWidth, { passive: true });
  nextTick(() => buildSwiper());
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateViewportWidth);
  destroySwiper(swiper, true, true);
});

return (_ctx: any,_cache: any) => {
  const _component_AdaptiveImage = _resolveComponent("AdaptiveImage")!
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (__props.fields && items.value.length)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value),
        id: __props.rendering?.dataSource
      }, [
        _createVNode(_component_ContentGrid, {
          "desktop-default-grid": [2, 20],
          "mobile-default-grid": [1, 14]
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", {
                ref_key: "swiperEl",
                ref: swiperEl,
                class: "swiper c-feature-carousel__swiper"
              }, [
                _createElementVNode("div", _hoisted_3, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item) => {
                    return (_openBlock(), _createElementBlock("article", {
                      key: item.id,
                      class: "swiper-slide c-feature-carousel__slide"
                    }, [
                      _createElementVNode("a", {
                        class: "c-feature-carousel__card",
                        href: item.href,
                        target: item.target,
                        rel: item.target === '_blank' ? 'noopener noreferrer' : undefined,
                        "aria-label": item.linkLabel
                      }, [
                        (item.imageField)
                          ? (_openBlock(), _createBlock(_component_AdaptiveImage, {
                              key: 0,
                              class: "c-feature-carousel__image",
                              field: item.imageField,
                              "is-background": false,
                              show: true,
                              "enable-scroll-detection": false,
                              "show-image-redius": false
                            }, null, 8, ["field"]))
                          : _createCommentVNode("", true),
                        _cache[0] || (_cache[0] = _createElementVNode("div", { class: "c-feature-carousel__shade" }, null, -1)),
                        _createElementVNode("div", _hoisted_5, [
                          (item.fields.title?.value)
                            ? (_openBlock(), _createBlock(_unref(RichText), {
                                key: 0,
                                class: "c-feature-carousel__title",
                                field: item.fields.title,
                                tag: "h3"
                              }, null, 8, ["field"]))
                            : _createCommentVNode("", true),
                          (item.fields.description?.value)
                            ? (_openBlock(), _createBlock(_unref(RichText), {
                                key: 1,
                                class: "c-feature-carousel__description",
                                field: item.fields.description
                              }, null, 8, ["field"]))
                            : _createCommentVNode("", true)
                        ]),
                        _cache[1] || (_cache[1] = _createElementVNode("span", {
                          class: "c-feature-carousel__entry-icon",
                          "aria-hidden": "true"
                        }, null, -1))
                      ], 8, _hoisted_4)
                    ]))
                  }), 128))
                ])
              ], 512),
              _withDirectives(_createElementVNode("div", {
                ref_key: "paginationEl",
                ref: paginationEl,
                class: "c-feature-carousel__pagination swiper-pagination"
              }, null, 512), [
                [_vShow, shouldShowPagination.value]
              ])
            ])
          ]),
          _: 1
        })
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})